import { useLingui } from '@lingui/react';
import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import AddElementBar from '../components/AddElementBar';
import Button from '../components/Button';
import CoverImageSection from '../components/CoverImageSection';
import ImageSection from '../components/ImageSection';
import Input from '../components/Input';
import LinkSection from '../components/LinkSection';
import RichTextEditor from '../components/RichTextEditor';
import Select from '../components/Select';
import TextSection from '../components/TextSection';
import { apiBaseUrl } from '../config';

import type { ImageUrls } from '../types/media';
import type { ContentElement, PublishState } from '../types/news';
import { NewsCategory } from '../types/news';
import { getFileExtension, uploadImage, uploadJson } from '../utils/fileUpload';

type StoredContentElement =
  | Exclude<ContentElement, { type: 'image' }>
  | (Omit<Extract<ContentElement, { type: 'image' }>, 'uploading'> & {
      uploading?: boolean;
    });

type StoredArticle = {
  id?: string;
  title?: string;
  category?: string | null;
  shortDescription?: string;
  coverImage?: ImageUrls | null;
  content?: StoredContentElement[];
  status?: string;
  published?: boolean;
  timestamps?: {
    createdAt?: string;
    updatedAt?: string;
    publishedAt?: string | null;
  };
  [key: string]: unknown;
};

export default function NewsEditPage() {
  const { i18n } = useLingui();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [title, setTitle] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [shortDescription, setShortDescription] = useState('');
  const [coverImageUrls, setCoverImageUrls] = useState<ImageUrls | null>(null);
  const [coverImageUploading, setCoverImageUploading] = useState(false);
  const [noCoverImage, setNoCoverImage] = useState(false);
  const [elements, setElements] = useState<ContentElement[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDirty, setIsDirty] = useState(false);
  const loadedArticle = useRef<StoredArticle>({});
  const initialSnapshot = useRef<string>('');
  const [publishState, setPublishState] = useState<PublishState>({
    status: 'idle',
  });

  useEffect(() => {
    if (!id) return;
    loadArticle(id);
  }, [id]);

  async function loadArticle(articleId: string): Promise<void> {
    try {
      const { data } = await axios.get<StoredArticle>(
        `${apiBaseUrl}/files/blogs/${articleId}.json`,
      );
      const category = data.category
        ? data.category.charAt(0).toUpperCase() + data.category.slice(1).toLowerCase()
        : null;

      setTitle(data.title ?? '');
      setSelectedCategory(category);
      setShortDescription(data.shortDescription ?? '');
      setCoverImageUrls(data.coverImage ?? null);
      setNoCoverImage(data.coverImage === null);
      const loadedElements: ContentElement[] = (data.content ?? []).map((el) =>
        el.type === 'image' ? { ...el, uploading: false } : el,
      );
      setElements(loadedElements);
      loadedArticle.current = data;
      initialSnapshot.current = JSON.stringify({
        title: data.title ?? '',
        category,
        shortDescription: data.shortDescription ?? '',
        coverImage: data.coverImage ?? null,
        noCoverImage: data.coverImage === null,
        elements: loadedElements,
      });
    } catch (err) {
      console.error('Fehler beim Laden', err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!initialSnapshot.current) return;
    const current = JSON.stringify({
      title,
      category: selectedCategory,
      shortDescription,
      coverImage: coverImageUrls,
      noCoverImage,
      elements,
    });
    setIsDirty(current !== initialSnapshot.current);
  }, [title, selectedCategory, shortDescription, coverImageUrls, noCoverImage, elements]);

  const isValid =
    title.trim() !== '' &&
    shortDescription.trim() !== '' &&
    selectedCategory !== null &&
    (noCoverImage || coverImageUrls !== null);

  function moveElement(index: number, direction: 'up' | 'down'): void {
    const updated = [...elements];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= updated.length) return;
    [updated[index], updated[swapIndex]] = [updated[swapIndex], updated[index]];
    setElements(updated);
  }

  function updateElement(id: string, updates: Partial<ContentElement>): void {
    setElements((prev) =>
      prev.map((el) =>
        el.id === id ? ({ ...el, ...updates } as ContentElement) : el,
      ),
    );
  }

  function deleteElement(id: string): void {
    setElements((prev) => prev.filter((el) => el.id !== id));
  }

  function addTextSection(): void {
    setElements((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        type: 'text',
        hasSubtitle: false,
        subtitle: '',
        text: '',
      },
    ]);
  }

  function addLinkSection(): void {
    setElements((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        type: 'link',
        linkDescription: '',
        url: '',
        showedText: '',
      },
    ]);
  }

  function addImageSection(): void {
    setElements((prev) => [
      ...prev,
      { id: crypto.randomUUID(), type: 'image', urls: null, uploading: false },
    ]);
  }

  async function handleCoverImageUpload(file: File): Promise<void> {
    setCoverImageUploading(true);
    try {
      const filename = `images/cover/${crypto.randomUUID()}.${getFileExtension(file.name)}`;
      setCoverImageUrls(await uploadImage(filename, file));
    } catch (err) {
      console.error('Cover-Upload fehlgeschlagen', err);
    } finally {
      setCoverImageUploading(false);
    }
  }

  function handleToggleNoCoverImage(): void {
    setNoCoverImage((prev) => !prev);
    setCoverImageUrls(null);
  }

  async function handleImageUpload(
    elementId: string,
    file: File,
  ): Promise<void> {
    updateElement(elementId, { uploading: true });
    try {
      const filename = `images/${crypto.randomUUID()}.${getFileExtension(file.name)}`;
      const urls = await uploadImage(filename, file);
      updateElement(elementId, { urls, uploading: false });
    } catch (err) {
      console.error('Bild-Upload fehlgeschlagen', err);
      updateElement(elementId, { uploading: false });
    }
  }

  async function handleSave(): Promise<void> {
    setPublishState({ status: 'loading' });
    try {
      const now = new Date().toISOString();
      const timestamps = loadedArticle.current.timestamps ?? {};
      const article = {
        ...loadedArticle.current,
        id,
        title,
        category: selectedCategory,
        shortDescription,
        coverImage: noCoverImage ? null : coverImageUrls,
        content: elements
          .filter((el) => el.type !== 'image' || el.urls !== null)
          .map((el) => {
            if (el.type === 'image') {
              const { uploading, ...rest } = el;
              return rest;
            }
            return el;
          }),
        timestamps: {
          ...timestamps,
          createdAt: timestamps.createdAt ?? timestamps.updatedAt ?? now,
          updatedAt: now,
          publishedAt: timestamps.publishedAt ?? null,
        },
      };

      await uploadJson(`blogs/${id}.json`, article);

      initialSnapshot.current = JSON.stringify({
        title,
        category: selectedCategory,
        shortDescription,
        coverImage: coverImageUrls,
        noCoverImage,
        elements,
      });
      loadedArticle.current = article;
      setIsDirty(false);
      setPublishState({ status: 'success' });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unbekannter Fehler';
      setPublishState({ status: 'error', message });
    }
  }

  const isSaving = publishState.status === 'loading';

  if (loading) {
    return (
      <div className="container-page padding-top-8">
        <p>{i18n._('common.loading')}</p>
      </div>
    );
  }

  return (
    <div className="container-page padding-top-8 padding-bottom-8">
      <div className="col-xs-12 col-lg-6">
        <div
          className="flex flex-col items-start gap-1 grow-1 w-full"
          style={{ maxWidth: '600px' }}
        >
          <h1>{i18n._('news.edit')}</h1>

          <CoverImageSection
            noCoverImage={noCoverImage}
            coverImageUrls={coverImageUrls}
            coverImageUploading={coverImageUploading}
            onToggleNoCoverImage={handleToggleNoCoverImage}
            onUpload={handleCoverImageUpload}
          />

          <div className="flex flex-col w-half">
            <label>{i18n._('news.category')}</label>
            <Select
              disabled={isSaving}
              placeholder={i18n._('news.categoryPlaceholder')}
              value={selectedCategory}
              options={[
                {
                  label: i18n._('news.category.news'),
                  value: NewsCategory.NEWS,
                },
                {
                  label: i18n._('news.category.events'),
                  value: NewsCategory.EVENTS,
                },
                {
                  label: i18n._('news.category.updates'),
                  value: NewsCategory.UPDATES,
                },
              ]}
              onChange={setSelectedCategory}
            />
          </div>

          <div className="border border-gray-tint-6 rounded-_20 padding-3 w-full margin-top-2">
            <h3>{i18n._('news.titleLabel')}</h3>
            <Input
              disabled={isSaving}
              value={title}
              maxlength={120}
              onChange={(value) => setTitle(value ?? '')}
              placeholder={i18n._('news.titlePlaceholder')}
            />
          </div>

          <div className="border border-gray-tint-6 rounded-_20 padding-3 w-full margin-top-2">
            <h3>{i18n._('news.shortDescription')}</h3>
            <RichTextEditor
              disabled={isSaving}
              value={shortDescription}
              onChange={setShortDescription}
              placeholder={i18n._('news.shortDescriptionPlaceholder')}
            />
          </div>

          {elements.map((element, index) => {
            if (element.type === 'text') {
              return (
                <TextSection
                  key={element.id}
                  element={element}
                  onUpdate={updateElement}
                  onDelete={deleteElement}
                  onMoveUp={() => moveElement(index, 'up')}
                  onMoveDown={() => moveElement(index, 'down')}
                  isFirst={index === 0}
                  isLast={index === elements.length - 1}
                />
              );
            }
            if (element.type === 'link') {
              return (
                <LinkSection
                  key={element.id}
                  element={element}
                  onUpdate={updateElement}
                  onDelete={deleteElement}
                  onMoveUp={() => moveElement(index, 'up')}
                  onMoveDown={() => moveElement(index, 'down')}
                  isFirst={index === 0}
                  isLast={index === elements.length - 1}
                />
              );
            }
            if (element.type === 'image') {
              return (
                <ImageSection
                  key={element.id}
                  element={element}
                  onUpload={handleImageUpload}
                  onDelete={deleteElement}
                  onMoveUp={() => moveElement(index, 'up')}
                  onMoveDown={() => moveElement(index, 'down')}
                  isFirst={index === 0}
                  isLast={index === elements.length - 1}
                />
              );
            }
            return null;
          })}

          <AddElementBar
            onAddText={addTextSection}
            onAddImage={addImageSection}
            onAddLink={addLinkSection}
          />

          <div className="flex gap-2 margin-top-2">
            <Button
              theme="secondary"
              disabled={isSaving}
              onClick={() => navigate('/news')}
            >
              {i18n._('common.back')}
            </Button>
            <Button onClick={handleSave} disabled={!isValid || !isDirty || isSaving}>
              {isSaving ? i18n._('common.saving') : i18n._('common.save')}
            </Button>
          </div>

          {publishState.status === 'success' && (
            <p style={{ color: 'green' }}>{i18n._('news.saveSuccess')}</p>
          )}
          {publishState.status === 'error' && (
            <p style={{ color: 'red' }}>
              {i18n._('news.saveError', { message: publishState.message })}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
